import React, { useContext, useEffect, useMemo, useState } from "react";
import {
  Search, ShoppingCart, X, Plus, Minus, CheckCircle2, AlertTriangle,
} from "lucide-react";
import { CategoryPlaceholder } from "../../../lib/consts/categoryPlaceholders";
import { UserContext } from "../../../context/UserContext";
import { useInVenueUsers } from "../../../context/useInVenueUsers";
import apiFetch, { safeJson, apiErrorDetail } from "../../../context/api";
import { getImageUrl } from "../../../lib/helpers";
import { effectiveUnitPrice } from "../DispenserComponents/pricing";
import AddCashModal from "../../Elements/AddCashModal";
import "./DispensarioPage.css";

/*
 * DispensarioPage
 * ───────────────
 * Dispensario (POS / retirada) page from the Figma redesign
 * (ClubAgent - UI, section node 3100:5014). Renders inside the real app shell
 * (Layout → SidebarBest + HeaderNew + <Outlet/>). Route: /dispensario, parallel
 * to the production /dispenser (which is left untouched).
 *
 * WIRED to real data (reuses the same sources as DispenserComponents/Dispenser):
 *   - Socios en sala   → useInVenueUsers().usersInVenue  (GET /v1/active-entries)
 *   - Catálogo         → GET /v1/get-venue-products?dispensario_only=true
 *   - Precio efectivo  → effectiveUnitPrice(product, socio.cost_line)
 *   - Crédito          → profile.credit_balance
 *   - Validar compra   → POST /v1/make-purchase (payment_method: 'credit')
 *
 * Known first-pass gaps (flagged): the member discount (terapéutico / perfil) is
 * NOT reflected in the displayed subtotal — the backend still applies it on
 * charge, so the amount actually cobrado can be lower than shown. Locations /
 * stock reservations / draft-cart persistence are not wired (make-purchase
 * validates stock server-side). Strain filter (Índica/Sativa/Híbrido) was
 * dropped since products carry no such field; category tabs do the real filter.
 *
 * Styles scoped under .df-page (this CRA build uses Bulma + per-component CSS).
 */

const AVATAR_COLORS = [
  "#6366f1", "#0ea5e9", "#10b981", "#f59e0b",
  "#ef4444", "#8b5cf6", "#ec4899", "#14b8a6",
];
function avatarColor(name) {
  let hash = 0;
  for (let i = 0; i < name.length; i += 1) hash = name.charCodeAt(i) + ((hash << 5) - hash);
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}
function initials(name) {
  return (name || "?").split(" ").filter(Boolean).slice(0, 2).map((p) => p.charAt(0)).join("").toUpperCase() || "?";
}
function euro(value) {
  return `€${(Math.round((Number(value) || 0) * 100) / 100).toFixed(2).replace(".", ",")}`;
}
function num(value) {
  const n = parseFloat(String(value ?? "").replace(",", "."));
  return Number.isFinite(n) ? n : 0;
}

/* Normalise a venue user (from /v1/active-entries) into the shape this page uses. */
function toSocio(u) {
  const p = u.profile || {};
  const name = [p.first_name || p.name, p.last_name].filter(Boolean).join(" ").trim() || u.email || "Socio";
  return {
    id: u.id,
    name,
    number: p.client_id ?? u.id,
    credit: Number(p.credit_balance ?? p.cash_balance ?? 0),
    costLine: p.cost_line || null,
    photo: getImageUrl(p.documents_path || p.image_path, null),
  };
}

const CATEGORY_ALL = "Todo";
function categoryLabel(name) {
  if (!name || ["Uncategorized", "uncategorized"].includes(name)) return "Sin categoría";
  return name;
}

function Avatar({ name, photo, square }) {
  const cls = `df-avatar${square ? " df-avatar--sq" : ""}`;
  if (photo) return <img className={cls} src={photo} alt={name} onError={(e) => { e.currentTarget.style.display = "none"; }} />;
  return <div className={cls} style={{ background: avatarColor(name) }}>{initials(name)}</div>;
}

function ProductThumb({ product, size = "lg" }) {
  const cls = size === "lg" ? "df-thumb" : "df-thumb df-thumb--mini";
  const src = product.image_path ? getImageUrl(product.image_path, null) : null;
  if (src) return <div className={`${cls} df-thumb--img`}><img src={src} alt={product.name} loading="lazy" onError={(e) => { e.currentTarget.parentElement.classList.remove("df-thumb--img"); e.currentTarget.remove(); }} /></div>;
  // Sin foto → ilustración de la categoría (Figma Club_CategoryPlaceholder).
  // En el carrito la miniatura es de 40px: ahí va la variante sm, que es solo
  // el icono. La lg lleva etiqueta y no cabe.
  return (
    <div className={`${cls} df-thumb--ph`}>
      <CategoryPlaceholder category={product.category?.name} size={size} />
    </div>
  );
}

export default function DispensarioPage({ demo = null }) {
  const demoMode = Boolean(demo);
  const [token, , , venueID] = useContext(UserContext);
  const { usersInVenue, updateUserCredit } = useInVenueUsers();

  const [products, setProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [loadError, setLoadError] = useState("");

  const [member, setMember] = useState(null);
  const [socioQuery, setSocioQuery] = useState("");
  const [socioOpen, setSocioOpen] = useState(false);

  const [category, setCategory] = useState(CATEGORY_ALL);
  const [prodQuery, setProdQuery] = useState("");

  const [draft, setDraft] = useState({});          // { [productId]: { peso, euro } }
  const [cart, setCart] = useState([]);            // [{ productId, qty }] qty = grams(weight)|units(fixed)
  const [cartOpen, setCartOpen] = useState(false);
  const [confirmClear, setConfirmClear] = useState(false);
  const [addCreditOpen, setAddCreditOpen] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [toast, setToast] = useState(null);

  /* ── fetch catalogue (dispensario products) ── */
  useEffect(() => {
    if (demoMode) {
      setProducts(demo.products || []);
      setLoadingProducts(false);
      setLoadError("");
      return undefined;
    }
    if (!token || !venueID) return undefined;
    let cancelled = false;
    (async () => {
      setLoadingProducts(true);
      setLoadError("");
      try {
        const PAGE = 200;
        let skip = 0;
        let all = [];
        // eslint-disable-next-line no-constant-condition
        while (true) {
          const res = await apiFetch(`/v1/get-venue-products?dispensario_only=true&skip=${skip}&limit=${PAGE}`, {
            headers: { Authorization: `Bearer ${token}`, "X-Venue-ID": venueID },
          });
          if (!res.ok) {
            const data = await res.json().catch(() => ({}));
            throw new Error(apiErrorDetail(data, `Error al cargar productos (${res.status})`));
          }
          const batch = await safeJson(res);
          const arr = Array.isArray(batch) ? batch : [];
          all = all.concat(arr);
          if (arr.length < PAGE) break;
          skip += PAGE;
        }
        if (!cancelled) setProducts(all);
      } catch (e) {
        if (!cancelled) setLoadError(e.message || "Error al cargar productos");
      } finally {
        if (!cancelled) setLoadingProducts(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token, venueID, demoMode, demo]);

  /* ── socios en sala ── */
  const socios = useMemo(
    () => (demoMode ? (demo.socios || []) : (usersInVenue || []).map(toSocio)),
    [demoMode, demo, usersInVenue]
  );
  const socioResults = useMemo(() => {
    const q = socioQuery.trim().toLowerCase();
    if (!q) return socios;
    return socios.filter((s) =>
      s.name.toLowerCase().includes(q) || String(s.number).toLowerCase().includes(q)
    );
  }, [socios, socioQuery]);

  // keep the selected member's credit in sync with the context (e.g. after add-cash).
  // Skipped in demo mode so a simulated purchase doesn't get reset from static data.
  useEffect(() => {
    if (demoMode || !member) return;
    const fresh = socios.find((s) => String(s.id) === String(member.id));
    if (fresh && fresh.credit !== member.credit) setMember((m) => ({ ...m, credit: fresh.credit }));
  }, [socios]); // eslint-disable-line react-hooks/exhaustive-deps

  function effPrice(product) {
    return Number(effectiveUnitPrice(product, member?.costLine)) || 0;
  }
  function isWeight(product) {
    return product.unit_type === "weight";
  }

  function selectMember(s) {
    setMember(s);
    setSocioOpen(false);
    setSocioQuery("");
    setCart([]);
    setDraft({});
    setCategory(CATEGORY_ALL);
    setProdQuery("");
  }
  function changeMember() {
    setMember(null);
    setCart([]);
    setCartOpen(false);
    setDraft({});
  }
  function showToast(message, kind = "ok") {
    setToast({ message, kind });
    window.clearTimeout(showToast._t);
    showToast._t = window.setTimeout(() => setToast(null), 3200);
  }

  function setPeso(product, raw) {
    const grams = num(raw);
    const euroVal = raw === "" ? "" : (grams * effPrice(product)).toFixed(2);
    setDraft((d) => ({ ...d, [product.id]: { peso: raw, euro: euroVal } }));
  }
  function setEuroField(product, raw) {
    const price = effPrice(product);
    const grams = raw === "" || price <= 0 ? "" : (num(raw) / price).toFixed(3);
    setDraft((d) => ({ ...d, [product.id]: { peso: grams, euro: raw } }));
  }
  function addToCart(product) {
    const add = isWeight(product) ? num(draft[product.id]?.peso) : 1;
    if (add <= 0) return;
    setCart((prev) => {
      const existing = prev.find((l) => l.productId === product.id);
      if (existing) {
        return prev.map((l) => (l.productId === product.id ? { ...l, qty: +(l.qty + add).toFixed(3) } : l));
      }
      return [...prev, { productId: product.id, qty: +add.toFixed(3) }];
    });
    showToast("Producto añadido");
  }
  function stepLine(productId, delta) {
    setCart((prev) =>
      prev
        .map((l) => (l.productId === productId ? { ...l, qty: +(l.qty + delta).toFixed(3) } : l))
        .filter((l) => l.qty > 0)
    );
  }
  function removeLine(productId) {
    setCart((prev) => prev.filter((l) => l.productId !== productId));
  }
  function clearCart() {
    setCart([]);
    setConfirmClear(false);
    setCartOpen(false);
    showToast("Carrito vaciado");
  }
  function addDemoCredit(amount) {
    setMember((m) => ({ ...m, credit: +(m.credit + amount).toFixed(2) }));
    showToast(`Crédito añadido: ${euro(amount)}`);
    setAddCreditOpen(false);
  }

  async function validatePurchase() {
    if (!member || cart.length === 0 || processing) return;
    if (demoMode) {
      // Simulate the charge locally (no backend): deduct from the socio's credit.
      setMember((m) => ({ ...m, credit: Math.max(0, +(m.credit - subtotal).toFixed(2)) }));
      setCart([]);
      setCartOpen(false);
      showToast("Compra validada (demo)");
      return;
    }
    setProcessing(true);
    try {
      const items = cartLines.map((l) => ({
        product_id: l.product.id,
        quantity: l.qty,
        unit_price: effPrice(l.product),
        ...(isWeight(l.product) ? { real_weight: l.qty } : {}),
      }));
      const res = await apiFetch("/v1/make-purchase", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          user_id: member.id,
          venue_id: venueID,
          items,
          payment_method: "credit",
        }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(apiErrorDetail(data, "Error al validar la compra"));
      }
      const data = await res.json().catch(() => ({}));
      const newBalance = data.new_balance != null ? Number(data.new_balance) : null;
      if (newBalance != null) {
        updateUserCredit(member.id, newBalance);
        setMember((m) => ({ ...m, credit: newBalance }));
      }
      setCart([]);
      setCartOpen(false);
      showToast("Compra validada");
    } catch (e) {
      showToast(e.message || "Error al validar la compra", "error");
    } finally {
      setProcessing(false);
    }
  }

  useEffect(() => {
    function onDocClick(e) {
      if (!e.target.closest(".df-socio-search")) setSocioOpen(false);
    }
    function onKey(e) {
      if (e.key === "Escape") { setSocioOpen(false); setConfirmClear(false); setCartOpen(false); }
    }
    document.addEventListener("click", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("click", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, []);

  /* ── categories from real products ── */
  const categories = useMemo(() => {
    const set = new Set([CATEGORY_ALL]);
    let hasUncat = false;
    products.forEach((p) => {
      const n = p.category?.name;
      if (n && !["Uncategorized", "uncategorized"].includes(n)) set.add(n);
      else hasUncat = true;
    });
    if (hasUncat) set.add("Sin categoría");
    return Array.from(set);
  }, [products]);

  useEffect(() => {
    if (category !== CATEGORY_ALL && !categories.includes(category)) setCategory(CATEGORY_ALL);
  }, [categories, category]);

  const visibleProducts = useMemo(() => products.filter((p) => {
    if (category !== CATEGORY_ALL) {
      if (category === "Sin categoría") {
        const n = p.category?.name;
        if (n && !["Uncategorized", "uncategorized"].includes(n)) return false;
      } else if (p.category?.name !== category) return false;
    }
    if (prodQuery && !(p.name || "").toLowerCase().includes(prodQuery.trim().toLowerCase())) return false;
    return true;
  }), [products, category, prodQuery]);

  const cartLines = cart
    .map((l) => ({ ...l, product: products.find((p) => String(p.id) === String(l.productId)) }))
    .filter((l) => l.product);
  const subtotal = cartLines.reduce((sum, l) => sum + l.qty * effPrice(l.product), 0);
  const disponible = member?.credit ?? 0;
  const falta = Math.max(0, subtotal - disponible);
  const insufficient = falta > 0;
  const totalUnits = cartLines.length;

  return (
    <div className="df-page">
      {/* ── member header: hero (no member) or banner (selected) ── */}
      {member ? (
        <div className="df-member-banner">
          <Avatar name={member.name} photo={member.photo} square />
          <div className="df-member">
            <span className="df-member-name">{member.name}</span>
            <span className="df-member-id">Nº Socio: {member.number} · Crédito: {euro(member.credit)}</span>
          </div>
          <button type="button" className="df-btn-outline" onClick={changeMember}>Cambiar socio</button>
        </div>
      ) : (
        <div className="df-hero">
          <div className="df-hero-text">
            <h1 className="df-hero-title">¿A quién atendemos?</h1>
            <p className="df-hero-sub">Busca un socio en sala para iniciar una retirada.</p>
          </div>
          <div className="df-socio-search">
            <label className="df-search" onClick={() => setSocioOpen(true)}>
              <Search size={16} />
              <input
                placeholder="Buscar socio"
                aria-label="Buscar socio"
                value={socioQuery}
                onFocus={() => setSocioOpen(true)}
                onChange={(e) => { setSocioQuery(e.target.value); setSocioOpen(true); }}
              />
            </label>
            {socioOpen && (
              <div className="df-socio-menu" role="listbox">
                {socioResults.length === 0 ? (
                  <div className="df-socio-empty">
                    {socios.length === 0 ? "No hay socios en sala." : `Sin resultados para “${socioQuery}”.`}
                  </div>
                ) : socioResults.map((s) => (
                  <button type="button" key={s.id} className="df-socio-item" role="option" aria-selected={false} onClick={() => selectMember(s)}>
                    <Avatar name={s.name} photo={s.photo} square />
                    <div className="df-member">
                      <span className="df-member-name">{s.name}</span>
                      <span className="df-member-id">
                        Nº Socio: {s.number} · Crédito: <span className={s.credit === 0 ? "is-zero" : ""}>{euro(s.credit)}</span>
                      </span>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── catalogue (disabled until a socio is selected) ── */}
      <div className={`df-catalogue${member ? "" : " is-disabled"}`}>
        <div className="df-tabs-row">
          {categories.map((c) => (
            <button
              type="button"
              key={c}
              className={`df-cat${category === c ? " is-active" : ""}`}
              onClick={() => member && setCategory(c)}
            >
              {c}
            </button>
          ))}
        </div>

        <div className="df-tools-row">
          <label className="df-search df-search--grow">
            <Search size={16} />
            <input
              placeholder="Buscar producto"
              aria-label="Buscar producto"
              value={prodQuery}
              onChange={(e) => setProdQuery(e.target.value)}
              disabled={!member}
            />
          </label>
        </div>

        {!member ? (
          <div className="df-catalogue-empty">
            <ShoppingCart size={72} strokeWidth={1} />
            <span className="df-catalogue-empty-title">Selecciona un socio</span>
            <span className="df-catalogue-empty-sub">El catálogo se activa al identificar al socio</span>
          </div>
        ) : loadingProducts ? (
          <div className="df-empty">Cargando productos…</div>
        ) : loadError ? (
          <div className="df-empty df-empty--error">{loadError}</div>
        ) : visibleProducts.length === 0 ? (
          <div className="df-empty">No hay productos para este filtro.</div>
        ) : (
          <div className="df-grid">
            {visibleProducts.map((p) => {
              const weight = isWeight(p);
              const d = draft[p.id] || { peso: "", euro: "" };
              const inCart = cart.some((l) => String(l.productId) === String(p.id));
              const active = weight ? num(d.peso) > 0 : inCart;
              const stock = p.available != null ? Number(p.available) : Number(p.stock_quantity);
              const outOfStock = Number.isFinite(stock) && stock <= 0;
              return (
                <div key={p.id} className={`df-card${active ? " is-active" : ""}`}>
                  <ProductThumb product={p} />
                  <span className="df-card-tag">{categoryLabel(p.category?.name)}</span>
                  <div className="df-card-name" title={p.name}>{p.name}</div>
                  <div className="df-card-price">
                    {effPrice(p).toFixed(2)} €/{weight ? "g" : "u"}
                    {Number.isFinite(stock) && (
                      <span className={`df-card-stock${outOfStock ? " is-out" : ""}`}>· Stock: {stock}{weight ? "g" : "u"}</span>
                    )}
                  </div>
                  {weight ? (
                    <div className="df-card-fields">
                      <label className="df-field">
                        <span>Peso</span>
                        <input inputMode="decimal" placeholder="0.0" value={d.peso} onChange={(e) => setPeso(p, e.target.value)} />
                      </label>
                      <label className="df-field">
                        <span>Euro</span>
                        <input inputMode="decimal" placeholder="0.0" value={d.euro} onChange={(e) => setEuroField(p, e.target.value)} />
                      </label>
                    </div>
                  ) : null}
                  <button
                    type="button"
                    className={`df-add${active ? " is-primary" : ""}`}
                    disabled={outOfStock}
                    onClick={() => addToCart(p)}
                  >
                    {outOfStock ? "Sin stock" : (<><Plus size={16} strokeWidth={2} />Añadir</>)}
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ── bottom total bar (sticky) ── */}
      {member && cartLines.length > 0 && !cartOpen && (
        <div className="df-bottombar">
          <div className="df-bottombar-total">
            <span className="df-bottombar-amount">Total: {euro(subtotal)}</span>
            <span className="df-bottombar-count">{totalUnits} producto{totalUnits === 1 ? "" : "s"} añadido{totalUnits === 1 ? "" : "s"}</span>
          </div>
          <button type="button" className="df-btn-primary" onClick={() => setCartOpen(true)}>
            <ShoppingCart size={16} /> Ver carrito
          </button>
        </div>
      )}

      {/* ─────────── Cart panel ─────────── */}
      {member && cartOpen && (
        <>
          <div className="df-cart-scrim" onClick={() => setCartOpen(false)} />
          <aside className="df-cart" role="dialog" aria-label="Carrito Dispensario">
            <div className="df-cart-head">
              <span className="df-cart-title">Carrito Dispensario</span>
              <button type="button" className="df-dialog-close" aria-label="Cerrar" onClick={() => setCartOpen(false)}><X size={16} /></button>
            </div>

            <div className="df-cart-member">
              <Avatar name={member.name} photo={member.photo} square />
              <div className="df-member">
                <span className="df-member-name">{member.name}</span>
                <span className="df-member-id">NºSocio: {member.number} · Activo</span>
              </div>
              <div className="df-cart-disponible">
                <span>DISPONIBLE</span>
                <strong>{euro(disponible)}</strong>
              </div>
            </div>

            <div className="df-cart-lines-head">
              <span>PRODUCTO</span>
              <span>CANTIDAD</span>
              <span>PRECIO</span>
            </div>

            <div className="df-cart-lines">
              {cartLines.length === 0 ? (
                <div className="df-empty">El carrito está vacío.</div>
              ) : cartLines.map((l) => {
                const weight = isWeight(l.product);
                return (
                  <div className="df-cart-line" key={l.productId}>
                    <div className="df-cart-line-info">
                      <ProductThumb product={l.product} size="sm" />
                      <div className="df-member">
                        <span className="df-member-name">{l.product.name}</span>
                        <span className="df-member-id">{euro(effPrice(l.product))} / {weight ? "g" : "u"}</span>
                      </div>
                    </div>
                    <div className="df-stepper">
                      <button type="button" aria-label="Restar" onClick={() => stepLine(l.productId, -1)}><Minus size={14} /></button>
                      <span>{weight ? `${l.qty}g` : l.qty}</span>
                      <button type="button" aria-label="Sumar" onClick={() => stepLine(l.productId, 1)}><Plus size={14} /></button>
                    </div>
                    <div className="df-cart-line-price">{euro(l.qty * effPrice(l.product))}</div>
                    <button type="button" className="df-cart-line-remove" aria-label="Quitar" onClick={() => removeLine(l.productId)}><X size={15} /></button>
                  </div>
                );
              })}
            </div>

            <div className="df-cart-summary">
              <div className="df-cart-summary-row">
                <span>Subtotal ({totalUnits} producto{totalUnits === 1 ? "" : "s"})</span>
                <strong>{euro(subtotal)}</strong>
              </div>
              <div className="df-cart-summary-row">
                <span>Saldo disponible</span>
                <strong className={insufficient ? "is-danger" : ""}>{euro(disponible)}</strong>
              </div>
            </div>
            <div className="df-cart-total-row">
              <span>Total</span>
              <strong>{euro(subtotal)}</strong>
            </div>

            {insufficient && (
              <div className="df-cart-warn">
                <div className="df-cart-warn-head">
                  <AlertTriangle size={16} /> Crédito insuficiente · Faltan {euro(falta)}
                </div>
                <p>El saldo disponible no cubre el total del carrito.</p>
                <button type="button" className="df-cart-warn-btn" onClick={() => setAddCreditOpen(true)}>
                  <Plus size={14} /> Añadir crédito al socio
                </button>
              </div>
            )}

            <button
              type="button"
              className="df-cart-validate"
              disabled={insufficient || cartLines.length === 0 || processing}
              onClick={validatePurchase}
            >
              {processing ? "Procesando…" : "Validar compra"}
            </button>
            {insufficient && <span className="df-cart-validate-hint">Añade crédito para poder validar</span>}
            <div className="df-cart-actions">
              <button type="button" className="df-btn-outline df-btn-outline--grow" onClick={() => setCartOpen(false)}>Seguir añadiendo</button>
              <button type="button" className="df-btn-danger" onClick={() => setConfirmClear(true)}>Vaciar carrito</button>
            </div>
          </aside>
        </>
      )}

      {/* ─────────── "Vaciar carrito" confirm ─────────── */}
      {confirmClear && (
        <div className="df-overlay" onClick={() => setConfirmClear(false)}>
          <div className="df-confirm" role="alertdialog" aria-modal="true" aria-label="Vaciar el carrito" onClick={(e) => e.stopPropagation()}>
            <span className="df-confirm-title">¿Vaciar el carrito?</span>
            <p className="df-confirm-sub">Se eliminarán todos los productos añadidos. Esta acción no se puede deshacer.</p>
            <div className="df-confirm-actions">
              <button type="button" className="df-btn-danger" onClick={clearCart}>Vaciar</button>
              <button type="button" className="df-btn-outline" onClick={() => setConfirmClear(false)}>Cancelar</button>
            </div>
          </div>
        </div>
      )}

      {/* ─────────── "Añadir crédito" ─────────── */}
      {addCreditOpen && demoMode && member && (
        <div className="df-overlay" onClick={() => setAddCreditOpen(false)}>
          <div className="df-confirm" role="dialog" aria-modal="true" aria-label="Añadir crédito" onClick={(e) => e.stopPropagation()}>
            <span className="df-confirm-title">Añadir crédito</span>
            <p className="df-confirm-sub">{member.name} · saldo actual {euro(member.credit)}</p>
            <div className="df-credit-quick">
              {[10, 20, 50, 100].map((a) => (
                <button type="button" key={a} className="df-credit-chip" onClick={() => addDemoCredit(a)}>+{euro(a)}</button>
              ))}
            </div>
            {falta > 0 && (
              <button type="button" className="df-cart-warn-btn df-credit-cover" onClick={() => addDemoCredit(Math.ceil(falta))}>
                <Plus size={14} /> Cubrir lo que falta ({euro(falta)})
              </button>
            )}
            <div className="df-confirm-actions">
              <button type="button" className="df-btn-outline" onClick={() => setAddCreditOpen(false)}>Cerrar</button>
            </div>
          </div>
        </div>
      )}
      {!demoMode && member && (
        <AddCashModal
          active={addCreditOpen}
          handleAddCashModal={() => setAddCreditOpen(false)}
          venueID={venueID}
          token={token}
          user_id={member.id}
          setErrorMessage={(msg) => msg && showToast(msg, "error")}
          onCashAdded={(uid, nb) => {
            const n = Number(nb);
            if (Number.isFinite(n)) {
              updateUserCredit(uid, n);
              setMember((m) => ({ ...m, credit: n }));
            }
          }}
        />
      )}

      {/* ─────────── toast ─────────── */}
      {toast && (
        <div className={`df-toast${toast.kind === "error" ? " df-toast--error" : ""}`} role="status">
          {toast.kind === "error" ? <AlertTriangle size={18} /> : <CheckCircle2 size={18} />}
          <span>{toast.message}</span>
        </div>
      )}
    </div>
  );
}
